import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardHeader, CardContent } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/badge';
import { Icon } from '@/shared/icons';
import { CREDIT_CATEGORIES, canonicalModelId, getModelDefinition } from '@troia-v3/types';
import type { CreditDashboardResponse, CreditCategory } from '../types';
import { PixQrCodeModal } from './PixQrCodeModal';
import { ChangePaymentMethodModal } from './ChangePaymentMethodModal';
import { CancelSubscriptionModal } from './CancelSubscriptionModal';
import { ReactivateSubscriptionModal } from './ReactivateSubscriptionModal';
import { AutoRechargeConfig } from './AutoRechargeConfig';
import { useAIModelConsumption } from '../hooks';
import { formatDateSafe } from '../utils/formatDateSafe';

interface CreditsDashboardProps {
  dashboard: CreditDashboardResponse | undefined;
  isLoading: boolean;
}

const numberFormat = new Intl.NumberFormat('pt-BR');
const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
};

const STATUS_MAP: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'destructive' }> = {
  active: { label: 'Ativo', variant: 'success' },
  pending: { label: 'Pendente', variant: 'warning' },
  awaiting_payment: { label: 'Aguardando Pagamento', variant: 'warning' },
  past_due: { label: 'Atrasado', variant: 'destructive' },
  suspended: { label: 'Suspenso', variant: 'destructive' },
  cancelled: { label: 'Cancelado', variant: 'default' },
};

const getCategoryLabel = (category: CreditCategory): string => {
  return CREDIT_CATEGORIES[category]?.label || category;
};

const MODEL_LABELS: Record<string, string> = {
  'claude-sonnet-4-20250514': 'Claude Sonnet 4',
  'claude-opus-4-20250514': 'Claude Opus 4',
  'claude-haiku-4-5-20251001': 'Claude Haiku 4.5',
  'claude-3-5-sonnet-20241022': 'Claude 3.5 Sonnet',
  'claude-3-5-haiku-20241022': 'Claude 3.5 Haiku',
  'gpt-4o': 'GPT-4o',
  'gpt-4o-mini': 'GPT-4o Mini',
  'gpt-4.1': 'GPT-4.1',
  'gpt-4.1-mini': 'GPT-4.1 Mini',
  'gpt-4.1-nano': 'GPT-4.1 Nano',
  'gemini-2.5-flash': 'Gemini 2.5 Flash',
  'gemini-2.5-pro': 'Gemini 2.5 Pro',
  'deepseek-chat': 'DeepSeek V3',
  'deepseek-reasoner': 'DeepSeek R1',
};

// Nome do catálogo primeiro (cobre modelo novo e slug do gateway, como
// `zai/glm-5.3-flash`); a lista fixa acima só sobra para ids antigos que
// saíram do catálogo.
const getModelLabel = (modelId: string): string => {
  return getModelDefinition(canonicalModelId(modelId))?.name || MODEL_LABELS[modelId] || modelId;
};

const MODEL_COLORS = [
  '#8b5cf6', // roxo
  '#3b82f6', // azul
  '#10b981', // verde
  '#f59e0b', // amarelo
  '#ef4444', // vermelho
  '#ec4899', // rosa
  '#06b6d4', // ciano
  '#f97316', // laranja
];

export const CreditsDashboard: React.FC<CreditsDashboardProps> = ({ dashboard, isLoading }) => {
  const navigate = useNavigate();
  const [pixModalOpen, setPixModalOpen] = useState(false);
  const [changePaymentModalOpen, setChangePaymentModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [reactivateModalOpen, setReactivateModalOpen] = useState(false);
  const { data: aiModels } = useAIModelConsumption();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-28 rounded-xl animate-pulse"
              style={{ backgroundColor: 'var(--theme-card-border)' }}
            />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="h-48 rounded-xl animate-pulse"
              style={{ backgroundColor: 'var(--theme-card-border)' }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <Card className="rounded-xl">
        <CardContent className="px-4 pb-4">
          <div className="text-center py-12">
            <Icon name="inbox" className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--theme-muted-text)' }} />
            <p style={{ color: 'var(--theme-muted-text)' }}>
              Nenhum dado de créditos disponivel
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const maxConsumption = dashboard.consumptionByCategory.length > 0
    ? Math.max(...dashboard.consumptionByCategory.map((c) => c.total))
    : 0;

  const statusInfo = dashboard.subscription
    ? STATUS_MAP[dashboard.subscription.status] || { label: dashboard.subscription.status, variant: 'default' as const }
    : null;

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Balance */}
        <Card className="rounded-xl">
          <CardHeader className="pb-2 py-3 px-4">
            <div className="flex items-center gap-2">
              <Icon name="zap" className="h-4 w-4" style={{ color: '#8b5cf6' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                Saldo de Créditos
              </span>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="text-2xl font-bold" style={{ color: 'var(--theme-card-text)' }}>
              {numberFormat.format(dashboard.balance)}
            </div>
          </CardContent>
        </Card>

        {/* Consumption */}
        <Card className="rounded-xl">
          <CardHeader className="pb-2 py-3 px-4">
            <div className="flex items-center gap-2">
              <Icon name="trending-down" className="h-4 w-4" style={{ color: 'var(--theme-warning-color)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                Consumo do Ciclo
              </span>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="text-2xl font-bold" style={{ color: 'var(--theme-card-text)' }}>
              {numberFormat.format(dashboard.totalCreditsOut)}
            </div>
          </CardContent>
        </Card>

        {/* Credits per cycle */}
        <Card className="rounded-xl">
          <CardHeader className="pb-2 py-3 px-4">
            <div className="flex items-center gap-2">
              <Icon name="package" className="h-4 w-4" style={{ color: 'var(--theme-success-color)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                Créditos Contratados
              </span>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold" style={{ color: 'var(--theme-card-text)' }}>
                {dashboard.subscription
                  ? numberFormat.format(dashboard.subscription.creditsPerCycle)
                  : '-'}
              </span>
              {dashboard.subscription?.status === 'pending' && (
                <Badge variant="warning">Pendente</Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Next renewal */}
        <Card className="rounded-xl">
          <CardHeader className="pb-2 py-3 px-4">
            <div className="flex items-center gap-2">
              <Icon name="calendar" className="h-4 w-4" style={{ color: 'var(--theme-info-color)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                Próxima Renovação
              </span>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="text-lg font-bold" style={{ color: 'var(--theme-card-text)' }}>
              {formatDateSafe(dashboard.subscription?.nextRenewalAt, DATE_OPTIONS)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Subscription card */}
        <Card className="rounded-xl">
          <CardHeader className="pb-2 py-3 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="layers" className="h-4 w-4" style={{ color: '#8b5cf6' }} />
                <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                  Assinatura Atual
                </span>
              </div>
              {statusInfo && (
                <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {dashboard.subscription ? (
              <div className="space-y-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-lg font-semibold" style={{ color: 'var(--theme-card-text)' }}>
                      {dashboard.subscription.planName}
                    </p>
                    {dashboard.subscription.pendingPlanChange && (
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: dashboard.subscription.pendingPlanChange.type === 'upgrade'
                            ? 'rgba(139, 92, 246, 0.1)'
                            : 'rgba(107, 114, 128, 0.1)',
                          color: dashboard.subscription.pendingPlanChange.type === 'upgrade'
                            ? '#8b5cf6'
                            : '#6b7280',
                        }}
                      >
                        {dashboard.subscription.pendingPlanChange.type === 'upgrade' ? 'upgrade' : 'downgrade'} para "{dashboard.subscription.pendingPlanChange.planName}"
                      </span>
                    )}
                  </div>
                  <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                    {numberFormat.format(dashboard.subscription.creditsPerCycle)} créditos/ciclo
                  </p>
                </div>

                <div
                  className="flex items-center gap-4 text-sm"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  <div className="flex items-center gap-1">
                    <Icon name="credit-card" className="h-3.5 w-3.5" />
                    <span>
                      {dashboard.subscription.paymentMethod === 'credit_card' ? 'Cartao' : 'PIX'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="clock" className="h-3.5 w-3.5" />
                    <span>
                      {formatDateSafe(dashboard.subscription.currentCycleStart, DATE_OPTIONS)}{' '}
                      -{' '}
                      {formatDateSafe(dashboard.subscription.currentCycleEnd, DATE_OPTIONS)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {dashboard.subscription.status === 'active' && (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate('/billing/plans')}
                      >
                        <Icon name="repeat" className="h-4 w-4 mr-2" />
                        Trocar Plano
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setChangePaymentModalOpen(true)}
                      >
                        <Icon name="credit-card" className="h-4 w-4 mr-2" />
                        Trocar Pagamento
                      </Button>
                    </>
                  )}
                  {(dashboard.subscription.status === 'active' ||
                    dashboard.subscription.status === 'past_due') && (
                    <Button
                      variant="error"
                      size="sm"
                      onClick={() => setCancelModalOpen(true)}
                    >
                      <Icon name="x-circle" className="h-4 w-4 mr-2" />
                      Cancelar Assinatura
                    </Button>
                  )}
                  {(dashboard.subscription.status === 'suspended' ||
                    dashboard.subscription.status === 'cancelled') && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setReactivateModalOpen(true)}
                    >
                      <Icon name="refresh" className="h-4 w-4 mr-2" />
                      Reativar Assinatura
                    </Button>
                  )}
                  {dashboard.subscription?.paymentMethod === 'pix' &&
                    dashboard.subscription?.status === 'pending' && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setPixModalOpen(true)}
                    >
                      <Icon name="smartphone" className="h-4 w-4 mr-2" />
                      Gerar PIX
                    </Button>
                  )}
                  {dashboard.subscription?.status === 'active' &&
                   dashboard.subscription?.pendingPlanChange?.type === 'upgrade' &&
                   dashboard.subscription?.pendingPlanChange?.pendingPaymentId && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setPixModalOpen(true)}
                    >
                      <Icon name="smartphone" className="h-4 w-4 mr-2" />
                      Gerar PIX
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-sm mb-3" style={{ color: 'var(--theme-muted-text)' }}>
                  Nenhuma assinatura ativa
                </p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/billing/plans')}
                >
                  Ver Planos
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Auto-recharge config (editable) */}
        <AutoRechargeConfig />
      </div>

      {/* Consumption widgets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Consumption by category */}
        {dashboard.consumptionByCategory.length > 0 && (
          <Card className="rounded-xl">
            <CardHeader className="pb-2 py-3 px-4">
              <div className="flex items-center gap-2">
                <Icon name="bar-chart" className="h-4 w-4" style={{ color: '#8b5cf6' }} />
                <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                  Consumo por Categoria
                </span>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="space-y-3">
                {dashboard.consumptionByCategory
                  .sort((a, b) => b.total - a.total)
                  .map((item) => {
                    const pct = maxConsumption > 0 ? (item.total / maxConsumption) * 100 : 0;
                    return (
                      <div key={item.category} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span style={{ color: 'var(--theme-card-text)' }}>
                            {getCategoryLabel(item.category)}
                          </span>
                          <span className="font-medium" style={{ color: 'var(--theme-card-text)' }}>
                            {numberFormat.format(item.total)}
                          </span>
                        </div>
                        <div
                          className="h-2 rounded-full overflow-hidden"
                          style={{ backgroundColor: 'var(--theme-card-border)' }}
                        >
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${pct}%`,
                              backgroundColor: '#8b5cf6',
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* AI Consumption by model — Pie Chart */}
        {aiModels && aiModels.length > 0 && (() => {
          const aiTotal = aiModels.reduce((sum, m) => sum + m.total, 0);
          const pieData = aiModels.map((item, index) => ({
            name: getModelLabel(item.model),
            value: item.total,
            input: item.input,
            output: item.output,
            color: MODEL_COLORS[index % MODEL_COLORS.length],
          }));

          return (
            <Card className="rounded-xl">
              <CardHeader className="pb-2 py-3 px-4">
                <div className="flex items-center gap-2">
                  <Icon name="cpu" className="h-4 w-4" style={{ color: '#8b5cf6' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                    Consumo por Modelo IA
                  </span>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="flex items-center gap-4">
                  {/* Donut chart */}
                  <div className="flex-shrink-0" style={{ width: 160, height: 160 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={70}
                          paddingAngle={2}
                          dataKey="value"
                          stroke="none"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => numberFormat.format(Number(value ?? 0))}
                          contentStyle={{
                            backgroundColor: 'var(--theme-card-background)',
                            borderColor: 'var(--theme-card-border)',
                            borderRadius: '8px',
                            color: 'var(--theme-card-text)',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Legend */}
                  <div className="flex-1 space-y-2 min-w-0">
                    {pieData.map((entry) => {
                      const pct = aiTotal > 0 ? ((entry.value / aiTotal) * 100).toFixed(1) : '0';
                      return (
                        <div key={entry.name} className="flex items-start gap-2 text-sm">
                          <div
                            className="flex-shrink-0 w-2.5 h-2.5 rounded-full mt-1"
                            style={{ backgroundColor: entry.color }}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate" style={{ color: 'var(--theme-card-text)' }}>
                                {entry.name}
                              </span>
                              <span className="flex-shrink-0 font-medium" style={{ color: 'var(--theme-card-text)' }}>
                                {pct}%
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                              <span>{numberFormat.format(entry.value)} créditos</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}
      </div>

      <PixQrCodeModal isOpen={pixModalOpen} onClose={() => setPixModalOpen(false)} />

      {dashboard?.subscription && (
        <ChangePaymentMethodModal
          isOpen={changePaymentModalOpen}
          onClose={() => setChangePaymentModalOpen(false)}
          subscription={dashboard.subscription}
        />
      )}

      {dashboard?.subscription && (
        <CancelSubscriptionModal
          isOpen={cancelModalOpen}
          onClose={() => setCancelModalOpen(false)}
          subscription={dashboard.subscription}
        />
      )}

      {dashboard?.subscription && (
        <ReactivateSubscriptionModal
          isOpen={reactivateModalOpen}
          onClose={() => setReactivateModalOpen(false)}
          subscription={dashboard.subscription}
        />
      )}
    </div>
  );
};

CreditsDashboard.displayName = 'CreditsDashboard';
